import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, Calendar, ChevronRight, ChevronLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Language } from '../App';
interface CurrentIssueProps {
  language: Language;
}
export function CurrentIssue({
  language
}: CurrentIssueProps) {
  const isPashto = language === 'pashto';
  const categories = isPashto ? ['مقالې', 'شاعري', 'علمي', 'سرمقالې'] : ['Articles', 'Poetry', 'Academic', 'Editorials'];
  const previousIssues = [{
    nameEn: 'Jan – Feb 2026',
    namePs: 'جنوري – فبروري ۲۰۲۶',
    volEn: 'Vol 21, Iss 1',
    volPs: 'ټوک ۲۱، ګڼه ۱',
    cover: 'https://cdn.magicpatterns.com/uploads/nkxSFVNX8PortnXbKmnDDH/image.png',
    link: '/jan-feb-2026'
  }, {
    nameEn: 'May 2016',
    namePs: 'مۍ ۲۰۱۶',
    volEn: 'Vol 11, Iss 3',
    volPs: 'ټوک ۱۱، ګڼه ۳',
    link: '/archive'
  }, {
    nameEn: 'Mar 2016',
    namePs: 'مارچ ۲۰۱۶',
    volEn: 'Vol 11, Iss 2',
    volPs: 'ټوک ۱۱، ګڼه ۲',
    link: '/archive'
  }, {
    nameEn: 'Jan 2016',
    namePs: 'جنوري ۲۰۱۶',
    volEn: 'Vol 11, Iss 1',
    volPs: 'ټوک ۱۱، ګڼه ۱',
    link: '/archive'
  }];
  return <section className="py-8 md:py-12 bg-gray-50 px-6 overflow-hidden" data-id="element-87">
      <div className="max-w-5xl mx-auto" data-id="element-88">
        <AnimatePresence mode="wait" data-id="element-89">
          <motion.div key={language} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.4
        }} data-id="element-90">
            {/* Section Header */}
            <div className="mb-8 md:mb-10 text-start" data-id="element-91">
              <h2 className={`text-3xl md:text-4xl font-bold text-pasoon-dark inline-block relative ${isPashto ? 'font-pashto' : 'font-serif'}`} data-id="element-92">
                {isPashto ? 'اوسنۍ ګڼه' : 'Current Issue'}
                <span className="absolute -bottom-3 start-0 w-1/2 h-1 bg-pasoon-red rounded-full" data-id="element-93"></span>
              </h2>
            </div>

            {/* Featured Issue Card */}
            <div className="bg-white rounded-2xl md:rounded-[2rem] shadow-xl shadow-gray-200/50 overflow-hidden flex flex-col md:flex-row mb-12" data-id="element-94">
              {/* Cover Image */}
              <div className="w-full md:w-2/5 lg:w-1/3 min-h-[300px] md:min-h-full relative overflow-hidden group" data-id="element-95">
                <img src="https://cdn.magicpatterns.com/uploads/sR4i2r4S1futQxXXFLun8U/Mar-April_26.jpg" alt={isPashto ? 'پاڅون مارچ – اپریل ۲۰۲۶' : 'Pasoon March – April 2026'} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-96" />
              </div>

              {/* Content */}
              <div className="w-full md:w-3/5 lg:w-2/3 p-8 md:p-12 flex flex-col justify-center text-start" data-id="element-97">
                <h3 className={`text-2xl md:text-3xl font-bold text-pasoon-dark mb-4 leading-tight ${isPashto ? 'font-pashto' : 'font-serif'}`} data-id="element-98">
                  {isPashto ? 'مارچ – اپریل ۲۰۲۶ ګڼه اوس شتون لري' : 'March – April 2026 Issue Now Available'}
                </h3>

                <p className={`text-gray-600 text-base md:text-lg mb-8 ${isPashto ? 'font-pashto leading-loose' : 'leading-relaxed'}`} data-id="element-99">
                  {isPashto ? 'دا ګڼه د ټولنیز کار، ادبي لیکنو، شاعرۍ او علمي څیړنو ځانګړې مقالې لري. اوس یې ولولئ او د پښتو ادب سره خپله مینه نوې کړئ.' : 'This issue features special articles on community work, literary writings, poetry, and academic research. Read it now and renew your love for Pashto literature.'}
                </p>

                {/* Categories */}
                <div className="mb-8 -mx-8 px-8 md:mx-0 md:px-0 overflow-x-auto scrollbar-hide" data-id="element-100">
                  <div className="flex gap-3 w-max" data-id="element-101">
                    {categories.map((cat, idx) => <span key={idx} className={`px-4 py-2 rounded-full bg-red-50 text-pasoon-red text-sm font-medium whitespace-nowrap border border-red-100 ${isPashto ? 'font-pashto' : ''}`} data-id="element-102">
                        {cat}
                      </span>)}
                  </div>
                </div>

                {/* CTA */}
                <div data-id="element-103">
                  <Link to="/current-issue" className={`inline-flex items-center justify-center gap-2 px-8 py-4 bg-pasoon-red text-white rounded-full font-medium text-lg hover:bg-red-800 transition-all shadow-lg shadow-red-900/20 hover:shadow-red-900/30 hover:-translate-y-0.5 active:translate-y-0 w-full sm:w-auto ${isPashto ? 'font-pashto' : 'font-sans'}`} data-id="element-104">
                    <BookOpen className="w-5 h-5" data-id="element-105" />
                    {isPashto ? 'بشپړه ګڼه ولولئ' : 'Read Full Issue'}
                  </Link>
                </div>
              </div>
            </div>

            {/* Previous Issues — Clean Cards */}
            <div data-id="element-106">
              <div className="flex items-center justify-between mb-8" data-id="element-107">
                <h3 className={`text-2xl font-bold text-pasoon-dark ${isPashto ? 'font-pashto' : 'font-serif'}`} data-id="element-108">
                  {isPashto ? 'تیرې ګڼې' : 'Previous Issues'}
                </h3>
                <Link to="/archive" className={`text-pasoon-red font-medium flex items-center gap-1 hover:text-red-800 transition-colors group ${isPashto ? 'font-pashto' : ''}`} data-id="element-109">
                  {isPashto ? 'آرشیف وګورئ' : 'View Archive'}
                  {isPashto ? <ChevronLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" data-id="element-110" /> : <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" data-id="element-111" />}
                </Link>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4" data-id="element-112">
                {previousIssues.map((issue, idx) => <motion.div key={idx} initial={{
                opacity: 0,
                y: 15
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: idx * 0.08
              }} className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all cursor-pointer" data-id="element-113">
                    <div className="flex items-center gap-3 mb-3" data-id="element-114">
                      <div className="w-10 h-10 rounded-lg bg-pasoon-red/10 flex items-center justify-center flex-shrink-0" data-id="element-115">
                        <Calendar className="w-5 h-5 text-pasoon-red" data-id="element-116" />
                      </div>
                      <div data-id="element-117">
                        <p className={`font-bold text-pasoon-dark ${isPashto ? 'font-pashto' : 'font-serif'}`} data-id="element-118">
                          {isPashto ? issue.namePs : issue.nameEn}
                        </p>
                        <p className={`text-gray-400 text-xs ${isPashto ? 'font-pashto' : ''}`} data-id="element-119">
                          {isPashto ? issue.volPs : issue.volEn}
                        </p>
                      </div>
                    </div>
                    <Link to={issue.link} className={`inline-flex items-center gap-1 text-pasoon-red text-sm font-medium hover:text-red-800 transition-colors ${isPashto ? 'font-pashto' : ''}`} data-id="element-120">
                      {isPashto ? 'ګڼه وګورئ' : 'View Issue'}
                      {isPashto ? <ChevronLeft className="w-3.5 h-3.5" data-id="element-121" /> : <ChevronRight className="w-3.5 h-3.5" data-id="element-122" />}
                    </Link>
                  </motion.div>)}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>;
}